import getConfig from 'next/config'

/**
 * EnvironmentName:
 * Enum matches env variable ENV.
 */
export enum EnvironmentName {
  local = 'local',
  smokebox = 'smokebox',
  stg = 'stg',
  sandbox = 'sandbox',
  prod = 'prod',
}

/**
 * EnvironmentMap:
 * Boolean entry for each env.
 */
export type EnvironmentMap = {
  [key in EnvironmentName]: boolean
}

/**
 * Value for environment helper.
 * @param valueMap - `{ local: false, smokebox true:, ...}`.
 * @param defaultValue - Default value.
 */
export function valueForEnvironment(
  valueMap: Partial<EnvironmentMap> = {},
  defaultValue = false,
): boolean {
  const config = getConfig()

  // Get env name from next config if exists
  // In the server `publicRuntimeConfig` may not be available, but `process.env` will be.
  const ENV: string = config?.publicRuntimeConfig.ENV ?? process.env.ENV ?? ''

  // Check if the env name exists
  if (ENV && ENV in valueMap) {
    return !!valueMap[ENV as EnvironmentName]
  }
  // fallback to default value
  return defaultValue
}

/**
 * Feature switch service:
 *
 * Controls access to feature per-environment (i.e., local, stg, prod).
 * In the future, we could connect to settings DB as well to manage features there.
 */
export const FeatureSwitch = {
  /**
   * Indicates if Segment events are tracked for the specified environment.
   */
  segmentEnabled() {
    return valueForEnvironment({
      smokebox: false,
      stg: false,
      sandbox: true,
      prod: true,
    })
  },

  /**
   * Indicates if the testnetExplorerLink should be used.
   */
  testExplorerLinkEnabled() {
    return valueForEnvironment({
      smokebox: true,
      sandbox: true,
      local: true,
    })
  },

  /**
   * Indicates if the sandbox signup flow is enabled.
   * Quick/reduced signup without Nova, sandbox bypass.
   */
  sandboxSignup() {
    return valueForEnvironment(
      {
        local: true,
        smokebox: true,
        sandbox: true,
      },
      false,
    )
  },

  /**
   * Indicates if the Sandbox badge on header is enabled for the specified environments.
   */
  sandboxBadgeEnabled() {
    return valueForEnvironment({
      smokebox: true,
      sandbox: true,
    })
  },

  /**
   * Example playground page.
   */
  devOnlyEnabled() {
    return valueForEnvironment({
      local: true,
      smokebox: true,
    })
  },

  /**
   * Rtp bank support enabled.
   */
  rtpBankSupportEnabled() {
    return valueForEnvironment({}, false)
  },

  /**
   * Indicates if new platform selection page is visible per environment.
   */
  platformSelectionEnabled() {
    return valueForEnvironment(
      {
        sandbox: false,
      },
      true,
    )
  },

  /**
   * Indicates if business account creation flow is enabled.
   */
  businessAccountCreationEnabled() {
    return valueForEnvironment(
      {
        local: true,
        smokebox: true,
        stg: true,
        prod: true,
      },
      false,
    )
  },

  /**
   * Indicates if we should provide notice in the Developer Dashboard about enabling product api via Circle Rep.
   */
  developerProductsDisabledMessage() {
    return valueForEnvironment(
      {
        stg: true,
        prod: true,
      },
      false,
    )
  },

  /**
   * Indicates if custody tour is enabled.
   * To ensure that it is disabled locally for Cypress tests to work consistently.
   */
  isCustodyTourEnabled() {
    return valueForEnvironment(
      {
        local: false,
      },
      true,
    )
  },

  /**
   * Permanent flag to allow lower environments to access account creation through a custom token.
   * Accessible via /invite-circle-mint/internal.
   */
  bypassCircleMintInviteToken() {
    return valueForEnvironment(
      {
        local: true,
        smokebox: true,
        stg: true,
      },
      false,
    )
  },

  /**
   * Indicates if the travel rule secure PII exchange is enabled.
   */
  isTravelRuleSecurePIIExchangeEnabled() {
    return valueForEnvironment({}, true)
  },
}
